import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Phone, MessageCircle, MessageSquare } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, Stat, Select, StatusPill } from "@/components/bits";

const TT = { contentStyle: { background: "#0F1612", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12, borderRadius: 8 } };
const COLORS = ["#22C55E", "#F59E0B", "#06B6D4", "#EA580C", "#10B981", "#94A3B8"];

const channelLabel = (ch) => (ch === "whatsapp" ? "WhatsApp" : ch === "voice_call" || ch === "call" ? "Call" : ch === "sms" ? "SMS" : ch === "voice_test" ? "Test call" : ch || "—");
const avgDuration = (sec) => { const s = Math.max(0, Math.round(sec || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const asText = (v) => (typeof v === "string" ? v : v && typeof v === "object" ? [v.text, v.detail, v.issue, v.note, v.title].filter(Boolean).join(" — ") || JSON.stringify(v) : String(v ?? ""));

const Metric = ({ label, value, accent = "text-slate-100" }) => (
  <div><p className="text-[10px] font-mono uppercase text-slate-500">{label}</p><p className={`font-display text-xl font-bold ${accent}`}>{value}</p></div>
);

const Bullets = ({ items, Icon, color }) => (
  <ul className="space-y-1.5">
    {items.map((w, i) => <li key={i} className="flex gap-2 text-sm text-slate-300 leading-relaxed"><Icon className={`w-3.5 h-3.5 mt-1 shrink-0 ${color}`} /><span>{asText(w)}</span></li>)}
  </ul>
);

export default function Insights() {
  const { company } = useAuth();
  const [state, setState] = useState("");
  const [campaign, setCampaign] = useState("");
  const [d, setD] = useState(null);
  const [journey, setJourney] = useState(null);
  const [journeyError, setJourneyError] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);

  useEffect(() => {
    const params = {};
    if (state) params.state = state;
    if (campaign) params.campaign_id = campaign;
    api.get("/insights", { params }).then((r) => setD(r.data));
  }, [state, campaign]);

  const loadJourney = useCallback(() => {
    if (!campaign) { setJourney(null); setJourneyError(false); return Promise.resolve(); }
    return api.get("/insights/journey", { params: { campaign_id: campaign } }).then((r) => { setJourney(r.data); setJourneyError(false); }).catch(() => setJourneyError(true));
  }, [campaign]);
  const loadAnalysis = useCallback(() => {
    if (!campaign) { setAnalysis(null); return Promise.resolve(); }
    return api.get(`/insights/journey/${campaign}/analysis`).then((r) => setAnalysis(r.data?.analysis || null)).catch(() => setAnalysis(null));
  }, [campaign]);
  useEffect(() => { loadJourney(); loadAnalysis(); }, [loadJourney, loadAnalysis]);
  useEffect(() => {
    if (!campaign) return undefined;
    const t = setInterval(() => loadJourney(), 5000);
    return () => clearInterval(t);
  }, [campaign, loadJourney]);

  const analyse = async () => {
    if (!campaign || analysing) return;
    setAnalysing(true);
    try {
      const { data } = await api.post(`/insights/journey/${campaign}/analysis`);
      setAnalysis(data?.analysis || null);
      toast.success("Analysis ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setAnalysing(false); }
  };

  const jc = journey?.campaign || {};
  const lj = journey?.leads || {};
  const gj = journey?.goal || {};
  const cm = journey?.call_metrics || {};
  const wm = journey?.whatsapp_metrics || {};
  const sms = journey?.sms_metrics || {};
  const seq = journey?.sequence || {};
  const runs = journey?.recent_runs || [];
  const jsm = jc.success_metric || {};
  const jTarget = jsm.target ?? 0;
  const jCurrent = jsm.current ?? 0;
  const jPct = jTarget ? Math.min(100, Math.round((jCurrent / jTarget) * 100)) : 0;

  return (
    <div>
      <PageHeader overline="Regional insights" title="What consumers want, by region" desc="Derived from conversation transcripts: products asked for, disease complaints, crops and sentiment per state / district." testId="insights-header"
        action={<div className="flex flex-col sm:flex-row gap-2"><Select value={campaign} onChange={setCampaign} options={(d?.campaigns || []).map((c) => ({ value: c.id, label: c.name }))} placeholder="All campaigns" testId="insights-campaign-select" className="w-56" /><Select value={state} onChange={setState} options={company?.states || []} placeholder="All states" testId="insights-state-select" className="w-48" /></div>} />

      <section className="mb-10" data-testid="journey-section">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <p className="overline-label">Campaign journey</p>
            <h2 className="font-display text-xl font-bold text-slate-100 mt-1">Lead progress & what went wrong</h2>
          </div>
          {journey && <p className="text-[11px] text-slate-500 hidden sm:block">{jc.agent_name}{jc.agent_name && jc.lead_type ? " · " : ""}{jc.lead_type}</p>}
        </div>

        {!campaign && (
          <div className="glass p-6 rise flex items-center gap-3" data-testid="journey-hint">
            <Sparkles className="w-4 h-4 text-emerald-300 shrink-0" />
            <p className="text-sm text-slate-400">Pick a campaign above to see its journey — leads in terminal state, goal achievement rate, call / WhatsApp / SMS metrics and the AI analysis of what went wrong.</p>
          </div>
        )}
        {campaign && !journey && (journeyError
          ? <div className="glass p-6 rise text-sm text-slate-500" data-testid="journey-error">Journey data is unavailable for this campaign right now. It will refresh automatically.</div>
          : <div className="glass p-6 rise text-sm text-slate-500" data-testid="journey-loading">Loading journey…</div>)}

        {campaign && journey && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Stat label="Leads terminal" value={lj.terminal ?? 0} sub={`of ${lj.total ?? 0} leads · ${lj.converted ?? 0} converted`} accent="slate" testId="journey-stat-terminal" />
              <Stat label="Goal achievements" value={`${gj.achieved ?? 0} · ${gj.rate ?? 0}%`} sub={`${gj.not_achieved ?? 0} not achieved · ${gj.pending ?? 0} pending`} accent="gold" testId="journey-stat-goal" />
              <Stat label="In progress" value={lj.in_progress ?? 0} sub={`${lj.pending ?? 0} pending · ${lj.queued ?? 0} queued`} accent="cyan" testId="journey-stat-inprogress" />
              <div className="glass p-5 rise" data-testid="journey-stat-status">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-3">Campaign status</p>
                <StatusPill status={jc.status} />
                <p className="text-xs text-slate-500 mt-3">{seq.total_steps ?? 0} sequence steps · {seq.leads_completed_sequence ?? 0} leads completed the sequence</p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="glass p-5 rise" data-testid="journey-call-metrics">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-emerald-400" /> Calls</p>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Attempts" value={cm.attempts ?? 0} />
                  <Metric label="Connected" value={cm.connected ?? 0} accent="text-emerald-300" />
                  <Metric label="Not connected" value={cm.not_connected ?? 0} />
                  <Metric label="Avg duration" value={avgDuration(cm.avg_duration_sec)} accent="text-cyan-300" />
                </div>
              </div>
              <div className="glass p-5 rise" data-testid="journey-whatsapp-metrics">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-[#25D366]" /> WhatsApp</p>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Sent" value={wm.sent ?? 0} />
                  <Metric label="Replied" value={wm.replied ?? 0} accent="text-emerald-300" />
                  <Metric label="Templates" value={wm.templates ?? 0} />
                  <Metric label="Simulated" value={wm.simulated ?? 0} accent="text-slate-400" />
                </div>
              </div>
              {sms.sent > 0 && (
                <div className="glass p-5 rise" data-testid="journey-sms-metrics">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5 text-cyan-300" /> SMS</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Metric label="Sent" value={sms.sent ?? 0} />
                    <Metric label="Replied" value={sms.replied ?? 0} accent="text-emerald-300" />
                  </div>
                </div>
              )}
              <div className="glass p-5 rise" data-testid="journey-success-progress">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-3">Success metric</p>
                <p className="font-display text-2xl font-bold text-emerald-300">{jCurrent}<span className="text-slate-500 text-lg">/{jTarget || "—"}</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">goal conversions · {jPct}% of target</p>
                <div className="h-1.5 rounded-full bg-white/10 mt-3 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-amber-400" style={{ width: `${jPct}%` }} /></div>
              </div>
            </div>

            <div className="grid lg:grid-cols-5 gap-5">
              <div className="glass p-5 rise lg:col-span-3" data-testid="journey-analysis">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <p className="overline-label flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> What went wrong — AI analysis</p>
                  <button onClick={analyse} disabled={analysing} className={analysis ? "btn-ghost btn-sm" : "btn-primary btn-sm"} data-testid="journey-analysis-btn">
                    {analysing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : analysis ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {analysis ? "Refresh" : "Analyse conversations"}
                  </button>
                </div>
                {analysis ? (
                  <div className="space-y-4">
                    {analysis.summary && <p className="text-sm text-slate-200 leading-relaxed">{analysis.summary}</p>}
                    {analysis.what_went_wrong?.length > 0 && (
                      <div><p className="text-[10px] font-mono uppercase tracking-wider text-rose-300 mb-2">What went wrong</p><Bullets items={analysis.what_went_wrong} Icon={AlertTriangle} color="text-rose-300" /></div>
                    )}
                    {analysis.top_objections?.length > 0 && (
                      <div><p className="text-[10px] font-mono uppercase tracking-wider text-amber-300 mb-2">Top objections</p><div className="flex flex-wrap gap-1.5">{analysis.top_objections.map((o, i) => <span key={i} className="pill-gold">{asText(o)}</span>)}</div></div>
                    )}
                    {analysis.improvements?.length > 0 && (
                      <div><p className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 mb-2">Improvements</p><Bullets items={analysis.improvements} Icon={CheckCircle2} color="text-emerald-300" /></div>
                    )}
                    {analysis.coaching_notes?.length > 0 && (
                      <div><p className="text-[10px] font-mono uppercase tracking-wider text-cyan-300 mb-2">Coaching notes</p><Bullets items={analysis.coaching_notes} Icon={Sparkles} color="text-cyan-300" /></div>
                    )}
                    <p className="text-[11px] text-slate-500 pt-3 border-t border-white/5">generated_by: {analysis.generated_by || "—"} · {analysis.conversations_analysed ?? 0} conversations · {analysis.generated_at ? new Date(analysis.generated_at).toLocaleString() : "—"}</p>
                  </div>
                ) : <p className="text-sm text-slate-500">No analysis yet. Run the AI over this campaign's conversations to see the summary, what went wrong, top objections and coaching fixes.</p>}
              </div>

              <div className="glass overflow-hidden rise lg:col-span-2" data-testid="journey-recent-runs">
                <p className="overline-label px-5 pt-5 mb-3">Recent runs</p>
                <div className="overflow-x-auto"><table className="w-full min-w-[520px]"><thead><tr><th className="table-head">Time</th><th className="table-head">Lead</th><th className="table-head">Channel</th><th className="table-head">Step</th><th className="table-head">Status</th><th className="table-head">Reason</th></tr></thead>
                  <tbody>
                    {runs.map((r) => <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`journey-run-${r.id}`}>
                      <td className="table-cell text-xs text-slate-500 whitespace-nowrap">{timeAgo(r.created_at)}</td>
                      <td className="table-cell text-xs text-slate-100">{r.lead_name || "—"}</td>
                      <td className="table-cell text-xs">{channelLabel(r.channel)}</td>
                      <td className="table-cell font-mono text-xs">{r.step ?? "—"}</td>
                      <td className="table-cell"><StatusPill status={r.status} /></td>
                      <td className="table-cell"><p className="text-xs text-slate-400 truncate max-w-[200px]" title={r.reason || ""}>{r.reason || "—"}</p></td>
                    </tr>)}
                    {!runs.length && <tr><td colSpan="6" className="table-cell text-center text-slate-500 py-8">No runs recorded yet.</td></tr>}
                  </tbody></table></div>
              </div>
            </div>
          </>
        )}
      </section>

      {d && d.total === 0 && <Empty title="No signal yet" desc="Run campaigns and the AI will aggregate requests, complaints and demand per region here." cta="Go to campaigns" to="/dashboard/campaigns" testId="insights-empty" />}
      {d && d.total > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Stat label="Calls made" value={d.calls_made ?? d.total} testId="istat-calls" />
            <Stat label="POs booked" value={d.orders_booked ?? 0} accent="gold" testId="istat-pos" />
            <Stat label="Conversion" value={`${d.conversion_rate ?? 0}%`} sub="POs ÷ calls made" accent="cyan" testId="istat-conversion" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat label="Conversations analysed" value={d.total} testId="istat-total" />
            <Stat label="Crop-stage confirm rate" value={`${d.confirm_rate}%`} accent="cyan" testId="istat-confirm" />
            <Stat label="Cropping corrections" value={d.cropping_corrections} sub="flagged for model improvement" accent="gold" testId="istat-corrections" />
            <Stat label="Top request" value={d.products[0]?.name || "—"} accent="slate" testId="istat-top" />
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="glass p-5 rise" data-testid="insights-products"><p className="overline-label mb-3">Most-requested products</p><div className="h-64"><ResponsiveContainer><BarChart data={d.products} layout="vertical" margin={{ left: 10 }}><XAxis type="number" hide /><YAxis type="category" dataKey="name" width={150} tick={{ fill: "#94A3B8", fontSize: 11 }} /><Tooltip {...TT} /><Bar dataKey="value" fill="#22C55E" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer></div></div>
            <div className="glass p-5 rise" data-testid="insights-diseases"><p className="overline-label mb-3">Top disease / pest complaints</p><div className="h-64"><ResponsiveContainer><BarChart data={d.diseases} layout="vertical" margin={{ left: 10 }}><XAxis type="number" hide /><YAxis type="category" dataKey="name" width={150} tick={{ fill: "#94A3B8", fontSize: 11 }} /><Tooltip {...TT} /><Bar dataKey="value" fill="#F59E0B" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer></div></div>
            <div className="glass p-5 rise grid grid-cols-2 gap-4" data-testid="insights-crops">
              <div><p className="overline-label mb-3">Crops</p><div className="h-44"><ResponsiveContainer><PieChart><Pie data={d.crops} dataKey="value" nameKey="name" innerRadius={35} outerRadius={65} paddingAngle={3}>{d.crops.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip {...TT} /></PieChart></ResponsiveContainer></div></div>
              <div><p className="overline-label mb-3">Sentiment</p><div className="h-44"><ResponsiveContainer><PieChart><Pie data={d.sentiment} dataKey="value" nameKey="name" innerRadius={35} outerRadius={65} paddingAngle={3}>{d.sentiment.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}</Pie><Tooltip {...TT} /></PieChart></ResponsiveContainer></div></div>
            </div>
            <div className="glass overflow-hidden rise" data-testid="insights-districts"><p className="overline-label px-5 pt-5 mb-3">District heat-list</p><table className="w-full"><thead><tr><th className="table-head">District</th><th className="table-head">Convs</th><th className="table-head">POs</th><th className="table-head">Top request</th></tr></thead><tbody>{d.districts.map((x) => <tr key={x.name} className="hover:bg-white/[0.02]"><td className="table-cell text-slate-100">{x.name}</td><td className="table-cell font-mono text-xs">{x.conversations}</td><td className="table-cell font-mono text-xs text-amber-300">{x.orders}</td><td className="table-cell text-xs">{x.top_request}</td></tr>)}</tbody></table></div>
          </div>
        </>
      )}
    </div>
  );
}
